'use client';

import { LinkButton } from '@/components/ui/Button/LinkButton';
import { useContactTracking } from '@/hooks/useContactTracking';
import { buildBoutiqueMapsUrl } from '@/lib/boutique/storeDisplayUtils';
import type { StoreHit } from '@/lib/algolia';
import { Link } from '@/lib/i18n/navigation';
import { boutiquePaths } from '@/lib/paths';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import styles from './BoutiqueCard.module.scss';

// ============================================
// Icons - Matching Breitling design (filled style)
// ============================================

function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>
  );
}

// ============================================
// Props
// ============================================

export interface BoutiqueCardProps {
  /** Store data from Algolia */
  store: StoreHit;
  /** Index for test IDs */
  index?: number;
}

// ============================================
// Component
// ============================================

/**
 * Boutique Card
 *
 * Displays a store/boutique with image, details, and CTAs.
 * Used in PlaceholderStores slider.
 * Light grey background, matching Breitling design.
 */
export function BoutiqueCard({ store, index }: BoutiqueCardProps) {
  const t = useTranslations('boutique');
  const { trackPhoneContact } = useContactTracking();

  // Get store image - prefer photo gallery, fall back to thumbnail
  const storeImage = store.photo_gallery?.[0]?.image?.url || store.thumbnail_url;

  // Get localized name/address (fall back to English versions)
  const name = store.name_en || store.name;
  const address = `${store.street}, ${store.city}`;

  const storeUrl = boutiquePaths.detail(store.slug);
  const appointmentUrl = boutiquePaths.appointment(store.slug);
  const mapsUrl = buildBoutiqueMapsUrl(store, store.street, store.city);

  return (
    <article className={styles.card} data-testid={`boutique-card-${index}`}>
      {/* Store Image */}
      <div className={styles.imageWrapper}>
        {storeImage ? (
          <Image
            src={storeImage}
            alt={name}
            fill
            className={styles.image}
            sizes="(max-width: 768px) 100vw, 400px"
          />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
      </div>

      {/* Store Info */}
      <div className={styles.info}>
        <div className={styles.details}>
          {/* Name */}
          <h3 className={styles.name}>{name}</h3>

          {/* Address */}
          <div className={styles.row}>
            <LocationIcon className={styles.icon} />
            <span className={styles.text}>{address}</span>
          </div>

          {/* Phone */}
          {store.phone && (
            <div className={styles.row}>
              <PhoneIcon className={styles.icon} />
              <a href={`tel:${store.phone}`} className={styles.text} onClick={trackPhoneContact}>
                {store.phone}
              </a>
            </div>
          )}
        </div>

        {/* CTAs - Matching Breitling design */}
        <div className={styles.ctas}>
          {/* Simple underlined text links */}
          <div className={styles.links}>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={styles.textLink}>
              {t('getDirections')}
            </a>
            <Link href={storeUrl} className={styles.textLink}>
              {t('viewMore')}
            </Link>
          </div>

          {/* Appointment button - only show if store offers appointments */}
          {store.offers_appointment && (
            <LinkButton
              href={appointmentUrl}
              variant="outline"
              size="md"
              fullWidth
              className={styles.button}
            >
              {t('bookAppointment')}
            </LinkButton>
          )}

          {/* View Details button for stores without appointments */}
          {!store.offers_appointment && (
            <LinkButton
              href={storeUrl}
              variant="outline"
              size="md"
              fullWidth
              className={styles.button}
            >
              {t('viewDetails')}
            </LinkButton>
          )}
        </div>
      </div>
    </article>
  );
}

BoutiqueCard.displayName = 'BoutiqueCard';
